import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Input, MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { Dismiss16Regular } from '@fluentui/react-icons';
import { MAX_RUN_COLLABORATORS, type UserSearchResult } from '@qcbot/shared';
import { searchUsers } from '../api';
import { getCloudDocumentUrl } from '../officeApi';
import type { RunCollabState } from '../useRunCollab';
import { UserAvatar } from './UserAvatar';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+$/;

export function ShareDialog({ open, onClose, state, meId }: { open: boolean; onClose: () => void; state: RunCollabState; meId: string | undefined }) {
  const cloudUrl = getCloudDocumentUrl();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      searchUsers(q)
        .then((users) => !cancelled && setResults(users))
        .catch(() => !cancelled && setResults([]))
        .finally(() => !cancelled && setSearching(false));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const taken = new Set(state.participants.map((p) => p.email.toLowerCase()));
  const options = results.filter((r) => !taken.has(r.email.toLowerCase()));
  const typedEmail = query.trim();
  const canAddTyped = EMAIL_PATTERN.test(typedEmail) && !taken.has(typedEmail.toLowerCase()) && !options.some((o) => o.email.toLowerCase() === typedEmail.toLowerCase());
  const onlineIds = new Set(state.online.map((p) => p.userId));
  const atLimit = state.participants.filter((p) => p.role === 'collaborator').length >= MAX_RUN_COLLABORATORS;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  async function add(email: string) {
    await run(async () => {
      await state.invite(email);
      setQuery('');
      setResults([]);
    });
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={{ margin: 16 }}>
        <DialogBody>
          <DialogTitle>Share this run</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!cloudUrl && (
              <MessageBar intent="warning">
                <MessageBarBody>Save this file to OneDrive or SharePoint first so collaborators can edit the same copy.</MessageBarBody>
              </MessageBar>
            )}

            {cloudUrl && state.canManage && (
              <MessageBar intent="info">
                <MessageBarBody>Also share the file itself with them from PowerPoint or Word (Share button, top right). Adding them here only gives access to the QC results.</MessageBarBody>
              </MessageBar>
            )}

            {state.canManage && cloudUrl && atLimit && (
              <MessageBar intent="info">
                <MessageBarBody>This run has reached the limit of {MAX_RUN_COLLABORATORS} collaborators. Remove one to add someone else.</MessageBarBody>
              </MessageBar>
            )}

            {state.canManage && cloudUrl && !atLimit && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Input
                  size="small"
                  placeholder="Add by name or email"
                  value={query}
                  disabled={busy}
                  onChange={(_, data) => setQuery(data.value)}
                  contentAfter={searching ? <Spinner size="extra-tiny" /> : undefined}
                />
                {canAddTyped && (
                  <Button size="small" appearance="subtle" style={{ justifyContent: 'flex-start' }} disabled={busy} onClick={() => void add(typedEmail)}>
                    Add {typedEmail}
                  </Button>
                )}
                {options.map((user) => (
                  <Button
                    key={user.email}
                    size="small"
                    appearance="subtle"
                    disabled={busy}
                    style={{ justifyContent: 'flex-start', height: 'auto', padding: '4px 6px' }}
                    onClick={() => void add(user.email)}
                  >
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                      <span style={{ fontSize: 12 }}>{user.displayName ?? user.email}</span>
                      <span style={{ fontSize: 11, color: '#666' }}>{[user.displayName ? user.email : '', user.title].filter(Boolean).join(' · ')}</span>
                    </span>
                  </Button>
                ))}
              </div>
            )}

            {error && (
              <div role="alert" style={{ fontSize: 12, color: '#b91c1c' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {state.participants.map((person) => {
                const isMe = person.userId === meId;
                const label = person.displayName || person.email;
                const removable = person.role === 'collaborator' && (state.canManage || isMe);
                return (
                  <div key={person.userId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <UserAvatar size={24} name={label} color={state.colorOf(person.userId)} online={onlineIds.has(person.userId) || isMe} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label}
                        {isMe ? ' (you)' : ''}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>{person.role === 'owner' ? 'Started this run' : 'Collaborator'}</div>
                    </div>
                    {removable && (
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={busy}
                        icon={isMe ? undefined : <Dismiss16Regular />}
                        title={isMe ? 'Leave this run' : 'Remove'}
                        onClick={() => void run(() => state.remove(person.userId))}
                      >
                        {isMe ? 'Leave' : undefined}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
